/**
 * «Обновление из файла» — ОДНА универсальная страница массового обновления признаков
 * по списку из Excel. Заменяет телефонный Data Updater («Импорт») и «Готовность из файла».
 *
 * Поток PREVIEW-FIRST (парсинг целиком в браузере, ничего не пишется до «Применить»):
 *   1. Загрузка .xlsx/.xlsm (dropzone + «Выбрать файл»).
 *   2. АВТОДЕТЕКТ КЛЮЧА: колонка «ID в БД» (числовая) → key_type='id'; иначе телефонная
 *      колонка («Мобильные телефоны» / «Телефон» / *тел / phone) -> key_type='phone'. Если есть
 *      обе — оператор переключает ключ RadioGroup'ом; всегда видно, какая колонка идёт в дело.
 *   3. ВЫБОР ПОЛЯ+ЗНАЧЕНИЯ через общий <FieldValuePicker> (то же, что в «Изменить признаки»):
 *      одно редактируемое поле + одно значение на ВЕСЬ файл. values = {field: buildValue(def, draft)}.
 *   4. СВОДКА: всего строк / валидных ключей (уникальных) / дубликаты / битые (без ключа).
 *      Для id число совпадений точное; для телефона точное станет известно при выполнении
 *      (резолв номер→volunteer_id идёт в самой задаче).
 *   5. ПОДТВЕРЖДЕНИЕ: галка боевой базы + кнопка «Применить (N → поле = значение)».
 *   6. applyMut.mutate({ key_type, keys, values }) → {job_id}; прогресс — во вкладке «Задачи»
 *      и в индикаторе в шапке.
 *
 * Контракт эндпоинта: POST /api/volunteers/bulk-edit-from-file/apply
 *   { key_type: 'id'|'phone', keys: number[]|string[], values: { <field>: <value> } } → { job_id }.
 * Для phone keys уходят КАК ЕСТЬ из файла (backend нормализует к последним 10 цифрам сам).
 *
 * РЕЖИМ «УИК → отделение»: если в файле есть колонка с номером УИК (findUikKey), страница
 * переключается в отдельный режим — вместо «поле+значение» показываем колонку ключа (id/телефон)
 * + колонку номера УИК. Клиент шлёт распарсенные строки [{key, uik_n}]; отделение (branch_id) и
 * uik_id вычисляет СЕРВЕР из refs.uiks по номеру УИК. Клиентское превью (по useRefs) — только
 * оценка (распознанные/неизвестные номера УИК); авторитетно пересчитывает сервер.
 *   Контракт: POST /api/volunteers/uik-branch-from-file/apply
 *   { key_type: 'id'|'phone', rows: [{ key: string, uik_n: number|string }] } → { job_id }.
 *
 * Визуальные токены/паттерны переиспользуют ReadinessImportPage для консистентности.
 */

import * as React from "react";
import * as XLSX from "xlsx";

import type { Refs } from "@/api/client";
import { useApplyBulkEditFile, useApplyUikBranchFile, useRefs } from "@/api/hooks";
import { AppHeader } from "@/components/AppHeader";
import { FieldValuePicker, buildValue } from "@/components/FieldValuePicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Pill } from "@/components/ui/Pill";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import {
  EDITABLE_FIELD_DEFS,
  FIELD_BY_KEY,
  boolOptions,
  resolveOptions,
  type FieldDef,
} from "@/lib/fields";

const BORDER = "var(--border-hex)";
const CARD_SHADOW = "0 1px 3px rgba(0,0,0,.04)";
const SAMPLE_SIZE = 15;
/** Сколько примеров неизвестных номеров УИК показать в сводке. */
const UNKNOWN_UIK_SAMPLE_SIZE = 8;

/** Как сопоставляем строки файла с активистами. */
type KeyType = "id" | "phone";

/* ───────────────────────── парсинг файла ───────────────────────── */

/** Заголовок → каноничная форма: схлопнуть пробелы/переносы, обрезать, lower. */
function normalizeHeader(h: string): string {
  return h.replace(/\s+/g, " ").trim().toLowerCase();
}

/** Найти ключ-колонку по точному заголовку, иначе по нормализованному совпадению. */
function findKey(keys: string[], target: string): string | null {
  const exact = keys.find((k) => k === target);
  if (exact) return exact;
  const t = normalizeHeader(target);
  return keys.find((k) => normalizeHeader(k) === t) ?? null;
}

/**
 * Ключ колонки «ID в БД» (volunteer_id). Точное 'ID в БД', иначе нормализованное
 * 'id в бд'. НЕ матчит колонку 'ID' (это UUID → 'id' != 'id в бд').
 */
function findIdKey(keys: string[]): string | null {
  return findKey(keys, "ID в БД");
}

/**
 * Ключ телефонной колонки. Сначала точные «Мобильные телефоны» / «Телефон», иначе
 * первая колонка, чей нормализованный заголовок содержит «телефон»/«phone»/«mobile»
 * ИЛИ отдельный токен «тел»/«тел.». Подстрока «тел» намеренно НЕ используется —
 * иначе ложно срабатывает на «избиратели», «житель», «родители», «показатель» (F51).
 */
const PHONE_TOKEN = /(^|[^а-яё])тел\.?([^а-яё]|$)/;
function findPhoneKey(keys: string[]): string | null {
  const direct = findKey(keys, "Мобильные телефоны") ?? findKey(keys, "Телефон");
  if (direct) return direct;
  return (
    keys.find((k) => {
      const n = normalizeHeader(k);
      if (n.includes("телефон") || n.includes("phone") || n.includes("mobile")) return true;
      return PHONE_TOKEN.test(n);
    }) ?? null
  );
}

/**
 * Ключ колонки с номером УИК. Сначала точные «УИК» / «№ УИК» / «Номер УИК», иначе
 * первая колонка, чей нормализованный заголовок содержит отдельный токен «уик»/«uik»
 * на границах слова. Подстрока НЕ используется — иначе ложно ловит «уикенд» и т.п.
 * (аналог PHONE_TOKEN/F51). Границей считаем всё, кроме букв (кир./лат.) и цифр.
 */
const UIK_TOKEN = /(^|[^а-яёa-z0-9])(уик|uik)([^а-яёa-z]|$)/;
function findUikKey(keys: string[]): string | null {
  const direct =
    findKey(keys, "УИК") ??
    findKey(keys, "№ УИК") ??
    findKey(keys, "Номер УИК") ??
    findKey(keys, "№ уик");
  if (direct) return direct;
  return keys.find((k) => UIK_TOKEN.test(normalizeHeader(k))) ?? null;
}

/**
 * Ячейка → строковый номер УИК как он есть (число → целочисленная строка «1004»,
 * строка → trimmed), либо null для пустой. Сервер сам сверяет со справочником refs.uiks.
 */
function cellToUikN(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

/** Ячейка → положительный целочисленный id, либо null (пусто/не число/дробь). */
function toNumericId(v: unknown): number | null {
  if (typeof v === "number") {
    return Number.isInteger(v) && v > 0 ? v : null;
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (s === "" || !/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  }
  return null;
}

/** Ячейка → непустая строка телефона КАК ЕСТЬ (обрезаны крайние пробелы), либо null. */
function cellToPhone(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

/**
 * Нормализовать телефон к последним 10 цифрам (mob хранит phone_mobile_digits = 10 цифр
 * без 7/8). Возвращает 10-значную строку или null, если цифр меньше 10. Используется только
 * для подсчёта уникальных/битых на клиенте — в задачу уходит СЫРАЯ строка телефона.
 */
function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.slice(-10);
}

/** «Фамилия Имя Отчество» из строки; пустые части пропускаем, иначе «—». */
function buildFio(
  row: Record<string, unknown>,
  famKey: string | null,
  imKey: string | null,
  otchKey: string | null,
): string {
  const parts = [famKey, imKey, otchKey]
    .map((k) => (k ? row[k] : null))
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v).trim());
  return parts.join(" ") || "—";
}

interface ParsedRow {
  fio: string;
  /** Числовой volunteer_id из «ID в БД», либо null. */
  id: number | null;
  /** Сырая строка телефона из телефонной колонки, либо null. */
  phone: string | null;
  /** Строковый номер УИК из УИК-колонки, либо null (режим «УИК → отделение»). */
  uikN: string | null;
}

interface FileParse {
  fileName: string;
  sheetName: string;
  hasIdColumn: boolean;
  hasPhoneColumn: boolean;
  /** Есть ли колонка с номером УИК → страница в режиме «УИК → отделение». */
  hasUikColumn: boolean;
  idColumnName: string | null;
  phoneColumnName: string | null;
  uikColumnName: string | null;
  totalRows: number;
  rows: ParsedRow[];
}

/** Разобрать книгу целиком на клиенте (без записи). */
function parseWorkbook(fileName: string, buf: ArrayBuffer): FileParse {
  const wb = XLSX.read(buf, { type: "array" });
  const preferred = wb.SheetNames.find((n) => normalizeHeader(n) === "избиратели");
  const sheetName = preferred ?? wb.SheetNames[0];
  const ws = sheetName ? wb.Sheets[sheetName] : undefined;

  const empty: FileParse = {
    fileName,
    sheetName: sheetName ?? "—",
    hasIdColumn: false,
    hasPhoneColumn: false,
    hasUikColumn: false,
    idColumnName: null,
    phoneColumnName: null,
    uikColumnName: null,
    totalRows: 0,
    rows: [],
  };
  if (!ws) return empty;

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: null });
  if (rows.length === 0) return empty;

  const keys = Object.keys(rows[0]);
  const idKey = findIdKey(keys);
  const phoneKey = findPhoneKey(keys);
  const uikKey = findUikKey(keys);
  const famKey = findKey(keys, "Фамилия");
  const imKey = findKey(keys, "Имя");
  const otchKey = findKey(keys, "Отчество");

  const parsedRows: ParsedRow[] = rows.map((row) => ({
    fio: buildFio(row, famKey, imKey, otchKey),
    id: idKey ? toNumericId(row[idKey]) : null,
    phone: phoneKey ? cellToPhone(row[phoneKey]) : null,
    uikN: uikKey ? cellToUikN(row[uikKey]) : null,
  }));

  return {
    fileName,
    sheetName: sheetName ?? "—",
    hasIdColumn: idKey != null,
    hasPhoneColumn: phoneKey != null,
    hasUikColumn: uikKey != null,
    idColumnName: idKey,
    phoneColumnName: phoneKey,
    uikColumnName: uikKey,
    totalRows: rows.length,
    rows: parsedRows,
  };
}

interface SampleRow {
  fio: string;
  /** Отображаемый ключ (id как строка / сырой телефон), либо null для «нет ключа». */
  keyText: string | null;
}

interface DerivedKeys {
  /** Ключи для отправки: number[] для id, string[] (сырые телефоны) для phone. */
  keys: (number | string)[];
  /** Уникальных валидных ключей. Для phone — уникальных по нормализованным 10 цифрам. */
  uniqueCount: number;
  duplicateCount: number;
  brokenCount: number;
  sample: SampleRow[];
}

/**
 * Свести распарсенные строки к ключам для выбранного key_type. Для 'id' дедуп по числу;
 * для 'phone' дедуп по нормализованным 10 цифрам, но в keys кладём СЫРУЮ строку (первое
 * вхождение) — backend нормализует сам.
 */
function deriveKeys(parse: FileParse, keyType: KeyType): DerivedKeys {
  const sample: SampleRow[] = [];
  let duplicateCount = 0;
  let brokenCount = 0;

  if (keyType === "id") {
    const seen = new Set<number>();
    const keys: number[] = [];
    for (const row of parse.rows) {
      const id = row.id;
      if (id == null) brokenCount++;
      else if (seen.has(id)) duplicateCount++;
      else {
        seen.add(id);
        keys.push(id);
      }
      if (sample.length < SAMPLE_SIZE) {
        sample.push({ fio: row.fio, keyText: id != null ? String(id) : null });
      }
    }
    return { keys, uniqueCount: keys.length, duplicateCount, brokenCount, sample };
  }

  const seen = new Set<string>();
  const keys: string[] = [];
  for (const row of parse.rows) {
    const raw = row.phone;
    const norm = raw != null ? normalizePhone(raw) : null;
    if (norm == null) brokenCount++;
    else if (seen.has(norm)) duplicateCount++;
    else {
      seen.add(norm);
      keys.push(raw as string);
    }
    if (sample.length < SAMPLE_SIZE) {
      sample.push({ fio: row.fio, keyText: norm != null ? (raw as string) : null });
    }
  }
  return { keys, uniqueCount: keys.length, duplicateCount, brokenCount, sample };
}

/* ─────────────────── режим «УИК → отделение» ─────────────────── */

/** Множество известных номеров УИК (поле `n` из refs.uiks) как строки — для превью. */
function buildUikNumberSet(refs: Refs | undefined): Set<string> {
  const set = new Set<string>();
  const uiks = refs?.uiks;
  if (Array.isArray(uiks)) {
    for (const u of uiks) {
      if (u && typeof u === "object") {
        const n = (u as Record<string, unknown>).n;
        if (n != null && n !== "") set.add(String(n));
      }
    }
  }
  return set;
}

interface UikSampleRow {
  fio: string;
  /** Отображаемый ключ (id/сырой телефон), либо null для «нет ключа». */
  keyText: string | null;
  /** Сырой номер УИК, либо null (пусто). */
  uikText: string | null;
  /** Найден ли номер УИК в справочнике refs.uiks. */
  uikKnown: boolean;
}

interface DerivedUikRows {
  /** Строки для отправки: [{key, uik_n}] по уникальным ключам (первое вхождение). */
  rows: { key: string; uik_n: string }[];
  /** Уникальных валидных ключей (для id — по числу, для phone — по 10 цифрам). */
  uniqueCount: number;
  duplicateCount: number;
  brokenCount: number;
  /** Среди уникальных строк: сколько номеров УИК распознано в refs.uiks. */
  uikKnownCount: number;
  /** Среди уникальных строк: сколько номеров УИК не распознано (вкл. пустые). */
  uikUnknownCount: number;
  /** Примеры первых нескольких неизвестных номеров УИК (без повторов). */
  unknownUikSamples: string[];
  sample: UikSampleRow[];
}

/**
 * Свести распарсенные строки к [{key, uik_n}] для режима «УИК → отделение». Дедуп по ключу
 * (id по числу / phone по нормализованным 10 цифрам), ключ в rows кладём СТРОКОЙ (id → String,
 * phone → сырой). Оценка распознанных/неизвестных номеров УИК — по клиентскому справочнику
 * `uikSet` (поле `n` refs.uiks); сервер пересчитывает авторитетно.
 */
function deriveUikRows(
  parse: FileParse,
  keyType: KeyType,
  uikSet: Set<string>,
): DerivedUikRows {
  const rows: { key: string; uik_n: string }[] = [];
  const sample: UikSampleRow[] = [];
  const unknownUikSamples: string[] = [];
  const unknownSeen = new Set<string>();
  const seen = new Set<string>();
  let duplicateCount = 0;
  let brokenCount = 0;
  let uikKnownCount = 0;
  let uikUnknownCount = 0;

  for (const row of parse.rows) {
    // Ключ + значение для дедупа, по выбранному key_type.
    let keyText: string | null = null;
    let dedupKey: string | null = null;
    if (keyType === "id") {
      if (row.id != null) {
        keyText = String(row.id);
        dedupKey = keyText;
      }
    } else if (row.phone != null) {
      const norm = normalizePhone(row.phone);
      if (norm != null) {
        keyText = row.phone;
        dedupKey = norm;
      }
    }

    const uikText = row.uikN;
    const uikKnown = uikText != null && uikSet.has(uikText);

    if (dedupKey == null) {
      brokenCount++;
    } else if (seen.has(dedupKey)) {
      duplicateCount++;
    } else {
      seen.add(dedupKey);
      rows.push({ key: keyText as string, uik_n: uikText ?? "" });
      if (uikKnown) {
        uikKnownCount++;
      } else {
        uikUnknownCount++;
        if (uikText != null && !unknownSeen.has(uikText)) {
          unknownSeen.add(uikText);
          if (unknownUikSamples.length < UNKNOWN_UIK_SAMPLE_SIZE) unknownUikSamples.push(uikText);
        }
      }
    }

    if (sample.length < SAMPLE_SIZE) {
      sample.push({ fio: row.fio, keyText, uikText, uikKnown });
    }
  }

  return {
    rows,
    uniqueCount: rows.length,
    duplicateCount,
    brokenCount,
    uikKnownCount,
    uikUnknownCount,
    unknownUikSamples,
    sample,
  };
}

/* ───────────────────────── вспомогательные ───────────────────────── */

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Неизвестная ошибка";
}

/** ru-RU grouped number (14 839). */
function fmtNum(n: number): string {
  return n.toLocaleString("ru-RU");
}

/** Человекочитаемая метка выбранного значения (для кнопки/сводки). «…» — не выбрано. */
function describeValue(def: FieldDef | undefined, draft: string, refs: Refs | undefined): string {
  if (!def) return "…";
  if (def.kind === "bool") {
    const val = draft === "n" ? "n" : "y";
    return boolOptions().find((o) => o.value === val)?.label ?? "—";
  }
  const opts = resolveOptions(def, refs);
  const labelOf = (v: string) => opts.find((o) => o.value === v)?.label ?? v;
  if (def.kind === "multiselect") {
    const parts = draft
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v !== "");
    if (parts.length === 0) return "…";
    return parts.map(labelOf).join(", ");
  }
  const t = draft.trim();
  return t === "" ? "…" : labelOf(t);
}

/** Детектор ключа по умолчанию: id важнее (точный), иначе телефон. */
function detectKeyType(parse: FileParse): KeyType {
  if (parse.hasIdColumn) return "id";
  if (parse.hasPhoneColumn) return "phone";
  return "id";
}

/* ───────────────────────── steps indicator ───────────────────────── */

type StepState = "active" | "future";

function StepBadge({ n, label, state }: { n: number; label: string; state: StepState }) {
  const active = state === "active";
  return (
    <span
      className="inline-flex items-center gap-[7px] text-[12.5px]"
      style={{
        color: active ? "var(--accent-weak-text)" : "var(--text-muted)",
        fontWeight: active ? 600 : 400,
      }}
    >
      <span
        className="flex items-center justify-center text-[11px]"
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: active ? "var(--accent-hex)" : BORDER,
          color: active ? "var(--on-accent)" : "var(--text-muted)",
        }}
      >
        {n}
      </span>
      {label}
    </span>
  );
}

function StepDivider() {
  return <span style={{ width: 24, height: 1.5, background: "var(--control-border)" }} aria-hidden />;
}

function SummaryRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-[var(--text-3)]">{label}</span>
      <span
        className="font-mono font-semibold tabular-nums"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

/* ───────────────────────── page ───────────────────────── */

export function UnifiedFileEditPage() {
  const { data: refs } = useRefs();
  const applyMut = useApplyBulkEditFile();
  const uikMut = useApplyUikBranchFile();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [parse, setParse] = React.useState<FileParse | null>(null);
  const [isParsing, setIsParsing] = React.useState(false);
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const [keyType, setKeyType] = React.useState<KeyType>("id");

  const [field, setField] = React.useState<string>(EDITABLE_FIELD_DEFS[0]?.field ?? "");
  const [draft, setDraft] = React.useState<string>(() =>
    FIELD_BY_KEY[EDITABLE_FIELD_DEFS[0]?.field ?? ""]?.kind === "bool" ? "y" : "",
  );
  const [acknowledged, setAcknowledged] = React.useState(false);

  // Режим «УИК → отделение» включается, если в файле есть колонка с номером УИК.
  const isUikMode = parse != null && parse.hasUikColumn;

  const applyResult = (isUikMode ? uikMut.data : applyMut.data) ?? null;
  const applyPending = isUikMode ? uikMut.isPending : applyMut.isPending;
  const applyIsError = isUikMode ? uikMut.isError : applyMut.isError;
  const applyError = isUikMode ? uikMut.error : applyMut.error;

  const def = FIELD_BY_KEY[field];
  const built = def ? buildValue(def, draft) : null;
  const hasValue = built != null;
  const valueLabel = describeValue(def, draft, refs);

  const uikNumberSet = React.useMemo(() => buildUikNumberSet(refs), [refs]);

  const derived = React.useMemo(
    () => (parse && !isUikMode ? deriveKeys(parse, keyType) : null),
    [parse, keyType, isUikMode],
  );
  const uikDerived = React.useMemo(
    () => (parse && isUikMode ? deriveUikRows(parse, keyType, uikNumberSet) : null),
    [parse, keyType, isUikMode, uikNumberSet],
  );

  function resetAll() {
    setFileName(null);
    setParse(null);
    setParseError(null);
    setAcknowledged(false);
    applyMut.reset();
    uikMut.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function ingestFile(file: File) {
    setFileName(file.name);
    setParse(null);
    setParseError(null);
    setAcknowledged(false);
    applyMut.reset();
    uikMut.reset();
    setIsParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const result = parseWorkbook(file.name, buf);
      setParse(result);
      setKeyType(detectKeyType(result));
    } catch (err) {
      setParseError(errMessage(err));
    } finally {
      setIsParsing(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void ingestFile(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void ingestFile(file);
  }

  function onFieldChange(next: string) {
    const nextDef = FIELD_BY_KEY[next];
    setField(next);
    setDraft(nextDef?.kind === "bool" ? "y" : "");
    setAcknowledged(false);
    applyMut.reset();
  }

  function handleApply() {
    if (isUikMode) {
      if (!parse || !uikDerived || uikDerived.uniqueCount === 0) return;
      // Клиент шлёт только {key, uik_n}; отделение (branch_id) вычисляет сервер из refs.uiks.
      uikMut.mutate({ key_type: keyType, rows: uikDerived.rows });
      return;
    }
    if (!parse || !derived || derived.uniqueCount === 0 || !def || built == null) return;
    applyMut.mutate({
      key_type: keyType,
      keys: derived.keys,
      values: { [def.field]: built },
    });
  }

  const parsed = parse != null;
  const bothColumns = !!parse?.hasIdColumn && !!parse?.hasPhoneColumn;
  const noKeyColumn = parsed && !parse?.hasIdColumn && !parse?.hasPhoneColumn;
  const uniqueCount = isUikMode
    ? (uikDerived?.uniqueCount ?? 0)
    : (derived?.uniqueCount ?? 0);
  const hasKeys = uniqueCount > 0;
  const isPhone = keyType === "phone";

  // В режиме УИК разрешаем «Применить», только если есть распознанные номера УИК —
  // иначе задача была бы пустой. Если справочник УИК в refs пуст (иная форма данных),
  // не блокируем: сервер пересчитает авторитетно.
  const uikRefsAvailable = uikNumberSet.size > 0;
  const uikHasKnown = (uikDerived?.uikKnownCount ?? 0) > 0;
  const uikNothingToWrite = isUikMode && uikRefsAvailable && !uikHasKnown;

  const canApply =
    hasKeys &&
    acknowledged &&
    !applyPending &&
    applyResult == null &&
    (isUikMode ? !uikNothingToWrite : hasValue);

  const keyColLabel = isPhone ? "Телефон" : "ID в БД";
  const PREVIEW_GRID = "minmax(0,1fr) 180px";
  const UIK_PREVIEW_GRID = "minmax(0,1fr) 150px 120px";

  return (
    <div className="flex h-screen flex-col bg-[var(--app-bg)]">
      <AppHeader />

      {/* Header + steps indicator */}
      <div
        className="shrink-0 border-b bg-[var(--panel)] px-[22px] pb-3 pt-[16px]"
        style={{ borderColor: BORDER }}
      >
        <div className="mx-auto max-w-[1180px]">
          <h1 className="m-0 text-[20px] font-semibold tracking-[-.01em]">Обновление из файла</h1>
          <p className="mt-1 text-[12.5px] text-[var(--text-3)]">
            {isUikMode ? (
              <>
                Режим «УИК → отделение»: обнаружена колонка с номером УИК. Каждому активисту из файла
                проставим УИК и соответствующее ему местное отделение (отделение вычисляет сервер по
                номеру УИК). Ключ сопоставления — «ID в БД» или телефон, определяется автоматически.
              </>
            ) : (
              <>
                Массовое выставление ОДНОГО признака всем активистам из Excel-файла. Ключ
                сопоставления определяется автоматически: колонка «ID в БД» (точный матч) либо телефон
                (резолв в номер→id при выполнении).
              </>
            )}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <StepBadge n={1} label="Загрузка" state="active" />
            <StepDivider />
            <StepBadge n={2} label="Сверка" state={parsed ? "active" : "future"} />
            <StepDivider />
            <StepBadge n={3} label="Применение" state={applyResult ? "active" : "future"} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-[22px] py-[20px]">
        <div className="mx-auto grid max-w-[1180px] gap-[20px]" style={{ gridTemplateColumns: "1fr 340px" }}>
          {/* ───────────── LEFT ───────────── */}
          <div className="flex flex-col gap-[16px]">
            {/* Dropzone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className="text-center"
              style={{
                background: "var(--panel)",
                border: `1.5px dashed ${isDragging ? "var(--accent-hex)" : "var(--control-border-3)"}`,
                borderRadius: 14,
                padding: 26,
              }}
            >
              <div
                className="mx-auto mb-3 flex items-center justify-center text-[22px]"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 11,
                  background: "var(--accent-weak)",
                  color: "var(--accent-hex)",
                }}
                aria-hidden
              >
                ↑
              </div>
              <div className="mb-1 text-[14px] font-semibold">Перетащите файл .xlsx или .xlsm</div>
              <div className="mb-[14px] text-[12.5px] text-[var(--text-muted)]">
                Нужна колонка «ID в БД» или колонка с телефоном · парсинг в браузере, ничего не
                отправляется до «Применить»
              </div>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isParsing}
                  className="inline-flex items-center justify-center text-[13px] font-semibold text-white disabled:opacity-60"
                  style={{
                    background: "var(--accent-hex)",
                    padding: "9px 16px",
                    borderRadius: 9,
                    cursor: isParsing ? "default" : "pointer",
                  }}
                >
                  {isParsing ? <Spinner className="mr-2 h-4 w-4" /> : null}
                  Выбрать файл
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleFileChange}
              />
              {parseError ? (
                <p className="mt-2 text-[12px] text-[var(--danger)]">
                  Не удалось разобрать файл: {parseError}
                </p>
              ) : null}
            </div>

            {/* Loaded file + key mapping + preview table */}
            {parse ? (
              <div
                className="overflow-hidden"
                style={{
                  background: "var(--panel)",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 14,
                  boxShadow: CARD_SHADOW,
                }}
              >
                {/* file header */}
                <div
                  className="flex items-center gap-3 px-[18px] py-[14px]"
                  style={{ borderBottom: "1px solid var(--divider)" }}
                >
                  <div
                    className="flex items-center justify-center text-[11px] font-bold"
                    style={{ width: 34, height: 34, borderRadius: 8, background: "var(--tone-green-bg)", color: "var(--success)" }}
                    aria-hidden
                  >
                    XLS
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{fileName ?? "файл.xlsx"}</div>
                    <div className="font-mono text-[11.5px] text-[var(--text-muted)]">
                      лист «{parse.sheetName}» · {fmtNum(parse.totalRows)} строк
                    </div>
                  </div>
                </div>

                {/* key mapping */}
                <div className="px-[18px] py-[14px]" style={{ borderBottom: "1px solid var(--divider)" }}>
                  <div className="mb-2 text-[12px] font-semibold text-[var(--text-1)]">
                    Ключ сопоставления
                  </div>
                  {noKeyColumn ? (
                    <p className="text-[12.5px] text-[var(--danger)]">
                      Не найдено ни колонки «ID в БД», ни колонки с телефоном. Матчинг невозможен —
                      добавьте один из этих столбцов. Колонка «ID» (UUID) не годится.
                    </p>
                  ) : bothColumns ? (
                    <RadioGroup
                      value={keyType}
                      onValueChange={(v) => setKeyType(v as KeyType)}
                      disabled={applyPending || applyResult != null}
                      className="gap-2"
                    >
                      <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                        <RadioGroupItem value="id" />
                        <span>
                          По «ID в БД»{" "}
                          <span className="text-[11.5px] text-[var(--text-muted)]">
                            (колонка «{parse.idColumnName}», точный матч)
                          </span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                        <RadioGroupItem value="phone" />
                        <span>
                          По телефону{" "}
                          <span className="text-[11.5px] text-[var(--text-muted)]">
                            (колонка «{parse.phoneColumnName}», резолв при выполнении)
                          </span>
                        </span>
                      </label>
                    </RadioGroup>
                  ) : parse.hasIdColumn ? (
                    <p className="text-[12.5px] text-[var(--text-2)]">
                      Ключ: <span className="font-semibold">«ID в БД»</span>{" "}
                      <span className="text-[var(--text-muted)]">
                        (колонка «{parse.idColumnName}», точный матч)
                      </span>
                    </p>
                  ) : (
                    <p className="text-[12.5px] text-[var(--text-2)]">
                      Ключ: <span className="font-semibold">телефон</span>{" "}
                      <span className="text-[var(--text-muted)]">
                        (колонка «{parse.phoneColumnName}», резолв номер→id при выполнении)
                      </span>
                    </p>
                  )}
                </div>

                {noKeyColumn ? null : isUikMode ? (
                  <>
                    {/* preview header row — режим «УИК → отделение» */}
                    <div
                      className="grid h-[34px] items-center px-[18px] text-[10.5px] font-semibold uppercase tracking-[.05em] text-[var(--text-muted)]"
                      style={{
                        gridTemplateColumns: UIK_PREVIEW_GRID,
                        borderBottom: `1px solid ${BORDER}`,
                        background: "var(--header-row)",
                      }}
                    >
                      <span>ФИО</span>
                      <span>{keyColLabel}</span>
                      <span>№ УИК</span>
                    </div>

                    {(uikDerived?.sample ?? []).map((row, i) => (
                      <div
                        key={`urow-${i}`}
                        className="grid h-[40px] items-center px-[18px] text-[12.5px]"
                        style={{ gridTemplateColumns: UIK_PREVIEW_GRID, borderBottom: "1px solid var(--border-soft)" }}
                      >
                        <span className="truncate font-medium">{row.fio}</span>
                        <span className="truncate">
                          {row.keyText != null ? (
                            <span className="font-mono text-[12px] text-[var(--text-2)]">
                              {row.keyText}
                            </span>
                          ) : (
                            <Pill tone="red" style={{ fontSize: 11, padding: "2px 8px" }}>
                              нет ключа
                            </Pill>
                          )}
                        </span>
                        <span className="truncate">
                          {row.uikText != null ? (
                            <span
                              className="font-mono text-[12px]"
                              style={{ color: row.uikKnown ? "var(--text-2)" : "var(--danger-2)" }}
                              title={row.uikKnown ? undefined : "Номер УИК не найден в справочнике"}
                            >
                              {row.uikText}
                              {row.uikKnown ? null : " ✕"}
                            </span>
                          ) : (
                            <span className="text-[var(--text-muted)]">—</span>
                          )}
                        </span>
                      </div>
                    ))}

                    {parse.totalRows > (uikDerived?.sample.length ?? 0) ? (
                      <div className="px-[18px] py-2.5 text-center text-[11.5px] text-[var(--text-muted)]">
                        … и ещё {fmtNum(parse.totalRows - (uikDerived?.sample.length ?? 0))} строк
                      </div>
                    ) : null}
                    {parse.totalRows === 0 ? (
                      <div className="px-[18px] py-6 text-center text-[12.5px] text-[var(--text-3)]">
                        В файле нет строк.
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    {/* preview header row */}
                    <div
                      className="grid h-[34px] items-center px-[18px] text-[10.5px] font-semibold uppercase tracking-[.05em] text-[var(--text-muted)]"
                      style={{
                        gridTemplateColumns: PREVIEW_GRID,
                        borderBottom: `1px solid ${BORDER}`,
                        background: "var(--header-row)",
                      }}
                    >
                      <span>ФИО</span>
                      <span>{keyColLabel}</span>
                    </div>

                    {(derived?.sample ?? []).map((row, i) => (
                      <div
                        key={`row-${i}`}
                        className="grid h-[40px] items-center px-[18px] text-[12.5px]"
                        style={{ gridTemplateColumns: PREVIEW_GRID, borderBottom: "1px solid var(--border-soft)" }}
                      >
                        <span className="truncate font-medium">{row.fio}</span>
                        <span className="truncate">
                          {row.keyText != null ? (
                            <span className="font-mono text-[12px] text-[var(--text-2)]">
                              {row.keyText}
                            </span>
                          ) : (
                            <Pill tone="red" style={{ fontSize: 11, padding: "2px 8px" }}>
                              нет ключа
                            </Pill>
                          )}
                        </span>
                      </div>
                    ))}

                    {parse.totalRows > (derived?.sample.length ?? 0) ? (
                      <div className="px-[18px] py-2.5 text-center text-[11.5px] text-[var(--text-muted)]">
                        … и ещё {fmtNum(parse.totalRows - (derived?.sample.length ?? 0))} строк
                      </div>
                    ) : null}
                    {parse.totalRows === 0 ? (
                      <div className="px-[18px] py-6 text-center text-[12.5px] text-[var(--text-3)]">
                        В файле нет строк.
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* ───────────── RIGHT ───────────── */}
          <div className="flex flex-col gap-[16px]">
            {/* Summary */}
            <div
              style={{
                background: "var(--panel)",
                border: `1px solid ${BORDER}`,
                borderRadius: 14,
                boxShadow: CARD_SHADOW,
                padding: 18,
              }}
            >
              <div className="mb-[14px] text-[14px] font-semibold">Сводка файла</div>
              {parse && isUikMode && uikDerived ? (
                <div className="flex flex-col gap-[11px]">
                  <SummaryRow label="Всего строк" value={fmtNum(parse.totalRows)} />
                  <SummaryRow
                    label="Активистов (уникальных)"
                    value={fmtNum(uikDerived.uniqueCount)}
                    valueColor={hasKeys ? "var(--success)" : "var(--danger-2)"}
                  />
                  <SummaryRow
                    label="Распознано номеров УИК"
                    value={fmtNum(uikDerived.uikKnownCount)}
                    valueColor={uikDerived.uikKnownCount > 0 ? "var(--success)" : "var(--danger-2)"}
                  />
                  <SummaryRow
                    label="Неизвестных УИК"
                    value={uikDerived.uikUnknownCount > 0 ? fmtNum(uikDerived.uikUnknownCount) : "—"}
                    valueColor={uikDerived.uikUnknownCount > 0 ? "var(--warn)" : undefined}
                  />
                  <SummaryRow
                    label="Дубликаты"
                    value={uikDerived.duplicateCount > 0 ? fmtNum(uikDerived.duplicateCount) : "—"}
                    valueColor={uikDerived.duplicateCount > 0 ? "var(--warn)" : undefined}
                  />
                  <SummaryRow
                    label="Строки без ключа (битые)"
                    value={uikDerived.brokenCount > 0 ? fmtNum(uikDerived.brokenCount) : "—"}
                    valueColor={uikDerived.brokenCount > 0 ? "var(--danger-2)" : undefined}
                  />
                  {uikDerived.unknownUikSamples.length > 0 ? (
                    <p className="text-[11.5px] text-[var(--text-muted)]">
                      Неизвестные номера (примеры):{" "}
                      <span className="font-mono text-[var(--danger-2)]">
                        {uikDerived.unknownUikSamples.join(", ")}
                        {uikDerived.uikUnknownCount > uikDerived.unknownUikSamples.length ? " …" : ""}
                      </span>
                      . Такие строки сервер пропустит и внесёт в отчёт.
                    </p>
                  ) : null}
                  <p className="text-[11.5px] text-[var(--text-muted)]">
                    Оценка по справочнику refs.uiks; отделение (branch_id) и uik_id считает СЕРВЕР по
                    номеру УИК и пересчитывает авторитетно
                    {isPhone
                      ? "; для телефона точное число совпадений станет известно при выполнении."
                      : "."}
                  </p>
                </div>
              ) : parse && derived ? (
                <div className="flex flex-col gap-[11px]">
                  <SummaryRow label="Всего строк" value={fmtNum(parse.totalRows)} />
                  <SummaryRow
                    label={isPhone ? "Валидных телефонов (уник.)" : "Валидных id (уникальных)"}
                    value={fmtNum(uniqueCount)}
                    valueColor={hasKeys ? "var(--success)" : "var(--danger-2)"}
                  />
                  <SummaryRow
                    label="Дубликаты"
                    value={derived.duplicateCount > 0 ? fmtNum(derived.duplicateCount) : "—"}
                    valueColor={derived.duplicateCount > 0 ? "var(--warn)" : undefined}
                  />
                  <SummaryRow
                    label="Строки без ключа (битые)"
                    value={derived.brokenCount > 0 ? fmtNum(derived.brokenCount) : "—"}
                    valueColor={derived.brokenCount > 0 ? "var(--danger-2)" : undefined}
                  />
                  {derived.duplicateCount > 0 ? (
                    <p className="text-[11.5px] text-[var(--text-muted)]">
                      Повторяющиеся ключи схлопнуты — обновление применится по уникальным.
                    </p>
                  ) : null}
                  {isPhone ? (
                    <p className="text-[11.5px] text-[var(--text-muted)]">
                      По телефону: {fmtNum(uniqueCount)} номеров уйдут в задачу, точное число
                      совпадений станет известно при выполнении (резолв номер→id внутри задачи;
                      неоднозначные и ненайденные попадут в отчёт задачи).
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-[12.5px] text-[var(--text-muted)]">
                  Загрузите файл, чтобы увидеть сводку.
                </p>
              )}
            </div>

            {/* Field + value + confirm + apply */}
            {parse && applyResult == null ? (
              <div
                className="flex flex-col gap-3"
                style={{
                  background: "var(--panel)",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 14,
                  boxShadow: CARD_SHADOW,
                  padding: 18,
                }}
              >
                <div className="text-[14px] font-semibold">
                  {isUikMode ? "УИК → отделение" : "Что записать"}
                </div>
                {isUikMode ? (
                  <>
                    <p className="text-[12px] text-[var(--text-3)]">
                      Каждому из {fmtNum(uniqueCount)}{" "}
                      {isPhone ? "телефонов" : "активистов"} из файла проставим УИК и соответствующее
                      ему местное отделение. Отделение вычисляет СЕРВЕР по номеру УИК из справочника —
                      клиент отправляет только ключ и номер УИК.
                    </p>
                    <div
                      className="flex flex-col gap-1 text-[12px]"
                      style={{
                        border: `1px solid ${BORDER}`,
                        background: "var(--header-row)",
                        borderRadius: 10,
                        padding: 12,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--text-3)]">Записывается</span>
                        <span className="font-semibold">УИК + местное отделение</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--text-3)]">Распознано УИК</span>
                        <span className="font-mono font-semibold tabular-nums text-[var(--success)]">
                          {fmtNum(uikDerived?.uikKnownCount ?? 0)}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                        Перед массовой записью сервер делает пробу на одном человеке и проверяет
                        результат; неизвестные номера УИК пропускаются с отчётом.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[12px] text-[var(--text-3)]">
                      Одно поле и одно значение применятся ко всем {fmtNum(uniqueCount)}{" "}
                      {isPhone ? "телефонам" : "активистам"} из файла.
                    </p>

                    <FieldValuePicker
                      fieldKey={field}
                      draftValue={draft}
                      onFieldChange={onFieldChange}
                      onValueChange={(v) => {
                        setDraft(v);
                        setAcknowledged(false);
                      }}
                    />
                  </>
                )}

                <div
                  className="flex items-start gap-2 text-[12px]"
                  style={{
                    border: "1px solid var(--tone-red-bg)",
                    background: "var(--tone-red-bg)",
                    borderRadius: 10,
                    padding: 12,
                  }}
                >
                  <span aria-hidden className="text-[var(--danger)]">
                    ⚠
                  </span>
                  <span className="text-[var(--danger)]">
                    Запись идёт в БОЕВУЮ базу и применяется немедленно.
                  </span>
                </div>

                <label className="flex items-center gap-2 text-[13px]">
                  <Checkbox
                    checked={acknowledged}
                    onCheckedChange={(c) => setAcknowledged(c === true)}
                    disabled={applyPending}
                    aria-label="Понимаю, что пишу в боевую базу"
                  />
                  <span>Понимаю, что пишу в боевую базу</span>
                </label>

                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!canApply}
                  className="mt-1 w-full text-[13.5px] font-semibold text-white disabled:opacity-50"
                  style={{
                    background: "var(--accent-hex)",
                    padding: 12,
                    borderRadius: 10,
                    cursor: canApply ? "pointer" : "not-allowed",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {applyPending ? <Spinner className="h-4 w-4" /> : null}
                  {isUikMode
                    ? `Применить (${fmtNum(uniqueCount)} → УИК + отделение)`
                    : `Применить (${fmtNum(uniqueCount)} → ${def?.label ?? "поле"} = ${valueLabel})`}
                </button>

                {!hasKeys ? (
                  <p className="text-center text-[11.5px] text-[var(--danger-2)]">
                    {noKeyColumn
                      ? "В файле нет колонки-ключа (id или телефон)."
                      : "В файле нет валидных ключей для выбранного способа сопоставления."}
                  </p>
                ) : isUikMode ? (
                  uikNothingToWrite ? (
                    <p className="text-center text-[11.5px] text-[var(--danger-2)]">
                      Ни один номер УИК из файла не найден в справочнике — записывать нечего. Проверьте
                      колонку с номером УИК и регион.
                    </p>
                  ) : null
                ) : !hasValue ? (
                  <p className="text-center text-[11.5px] text-[var(--text-muted)]">
                    Выберите поле и значение.
                  </p>
                ) : null}
                {applyIsError ? (
                  <p className="text-[11.5px] text-[var(--danger)]">
                    Ошибка запуска: {errMessage(applyError)}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* Success */}
            {applyResult ? (
              <div
                className="flex flex-col gap-2"
                style={{
                  background: "var(--panel)",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 14,
                  boxShadow: CARD_SHADOW,
                  padding: 18,
                }}
              >
                <div className="flex items-center gap-2 text-[14px] font-semibold text-[var(--success)]">
                  ✓ Задача запущена
                </div>
                {isUikMode ? (
                  <p className="text-[12.5px] text-[var(--text-3)]">
                    УИК и местное отделение проставляются для {fmtNum(uniqueCount)}{" "}
                    {isPhone ? "телефонов" : "активистов"} из файла. Задача{" "}
                    <span className="font-mono">#{applyResult.job_id}</span> — прогресс виден во
                    вкладке «Задачи» и в индикаторе в шапке. Сервер сначала делает пробу на одном
                    человеке; неизвестные номера УИК{isPhone ? ", ненайденные и неоднозначные номера" : ""}{" "}
                    попадут в отчёт задачи.
                  </p>
                ) : (
                  <p className="text-[12.5px] text-[var(--text-3)]">
                    Поле «{def?.label ?? field}» = «{valueLabel}» выставляется для {fmtNum(uniqueCount)}{" "}
                    {isPhone ? "телефонов" : "активистов"}. Задача{" "}
                    <span className="font-mono">#{applyResult.job_id}</span> — прогресс виден во
                    вкладке «Задачи» и в индикаторе в шапке
                    {isPhone
                      ? "; ненайденные и неоднозначные номера попадут в отчёт задачи."
                      : "."}
                  </p>
                )}
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-1 w-full text-[13px] font-semibold text-[var(--text-2)] disabled:opacity-50"
                  style={{
                    background: "var(--panel)",
                    border: `1px solid ${BORDER}`,
                    padding: 10,
                    borderRadius: 10,
                    cursor: "pointer",
                  }}
                >
                  Загрузить следующий файл
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
